import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  GroupServiceTypes,
  IndividualServiceTypes,
  ServiceLabels,
  type CohortDto,
  type CreateCohortInput,
  type GroupServiceType,
  type IndividualServiceType,
  type ServiceType,
} from '@census/shared';
import { api } from '../../shared/api';
import { useAuth } from '../../shared/auth';
import { Modal } from '../../ui/Modal';
import { Icon } from '../../ui/Icon';

/**
 * Master Library landing page. First entry is Cohorts; additional master tables
 * can be added as sections inside the same page as we promote more hardcoded
 * enums to CRUD-able data (services, rooms types, etc.).
 *
 * Mutations are admin-only — the API enforces it; we hide controls for
 * non-admins so the surface looks intentional.
 */
export function MasterDataPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Master library</h1>
        <p className="text-sm text-ink-subtle">
          Configurable lookup tables that drive the scheduler. {isAdmin ? 'Admin edits are applied immediately.' : 'Admin role required to edit.'}
        </p>
      </header>
      <CohortsSection isAdmin={isAdmin} />
    </div>
  );
}

// ─── Cohorts CRUD section ─────────────────────────────────────────────────
function CohortsSection({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<CohortDto | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['cohorts'],
    queryFn: () => api.get<CohortDto[]>('/cohorts').then((r) => r.data),
  });

  const remove = useMutation({
    mutationFn: (code: string) => api.delete(`/cohorts/${code}`).then((r) => r.data),
    onSuccess: () => {
      toast.success('Cohort deleted');
      qc.invalidateQueries({ queryKey: ['cohorts'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Delete failed'),
  });

  return (
    <section className="card p-4">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">Cohorts</h2>
          <p className="text-2xs text-ink-subtle">
            One row per cohort code. Drives required group placement at intake and the per-cohort
            default required-individual list. Inactive cohorts stay assignable to existing clients
            but are hidden from new intakes.
          </p>
        </div>
        {isAdmin && (
          <button className="btn-primary h-9" onClick={() => setCreating(true)}>
            + New cohort
          </button>
        )}
      </header>

      {isLoading ? (
        <p className="text-sm text-ink-subtle">Loading…</p>
      ) : !data || data.length === 0 ? (
        <p className="text-sm text-ink-subtle">No cohorts. Click "+ New cohort" to add one.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle text-2xs uppercase tracking-wide text-ink-subtle">
              <tr>
                <th className="px-3 py-2 text-left">Code</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Required groups</th>
                <th className="px-3 py-2 text-left">Default individual services</th>
                <th className="px-3 py-2 text-right">Max</th>
                <th className="px-3 py-2 text-right">Sort</th>
                <th className="px-3 py-2 text-left">Active</th>
                {isAdmin && <th className="px-3 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.id} className="border-t border-line-subtle hover:bg-surface-subtle/40">
                  <td className="px-3 py-2 font-mono font-semibold text-brand-700">{c.code}</td>
                  <td className="px-3 py-2">{c.name}</td>
                  <td className="px-3 py-2 text-xs">
                    {c.requiredGroups.length === 0 ? (
                      <span className="text-ink-subtle">—</span>
                    ) : (
                      c.requiredGroups.map((g) => (
                        <span key={g} className="mr-1 inline-block rounded bg-brand-50 px-1.5 py-0.5 text-2xs text-brand-700">
                          {ServiceLabels[g as ServiceType] ?? g}
                        </span>
                      ))
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {c.defaultIndividualServices.length === 0 ? (
                      <span className="text-ink-subtle">—</span>
                    ) : (
                      c.defaultIndividualServices.map((s) => (
                        <span key={s} className="mr-1 inline-block rounded bg-emerald-50 px-1.5 py-0.5 text-2xs text-emerald-700">
                          {ServiceLabels[s as ServiceType] ?? s}
                        </span>
                      ))
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {c.maxCapacity ?? <span className="text-ink-subtle">∞</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.sortOrder}</td>
                  <td className="px-3 py-2 text-xs">
                    {c.active ? <span className="pill-success">Active</span> : <span className="pill-muted">Inactive</span>}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button className="btn-ghost h-7 px-2 text-xs" onClick={() => setEditing(c)}>
                          Edit
                        </button>
                        <button
                          className="btn-ghost h-7 px-2 text-xs text-rose-700"
                          onClick={() => {
                            if (confirm(`Delete cohort "${c.code}"? (refused if any client is assigned)`)) {
                              remove.mutate(c.code);
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CohortEditModal
          mode="create"
          initial={{
            code: '',
            name: '',
            description: null,
            requiredGroups: [],
            defaultIndividualServices: [],
            maxCapacity: null,
            sortOrder: 100,
            active: true,
          }}
          onClose={() => setCreating(false)}
          onSave={async (form) => {
            await api.post('/cohorts', form);
            qc.invalidateQueries({ queryKey: ['cohorts'] });
            setCreating(false);
            toast.success('Cohort created');
          }}
        />
      )}
      {editing && (
        <CohortEditModal
          mode="edit"
          codeOriginal={editing.code}
          initial={{
            code: editing.code,
            name: editing.name,
            description: editing.description ?? null,
            requiredGroups: editing.requiredGroups as GroupServiceType[],
            defaultIndividualServices: editing.defaultIndividualServices as IndividualServiceType[],
            maxCapacity: editing.maxCapacity ?? null,
            sortOrder: editing.sortOrder,
            active: editing.active,
          }}
          onClose={() => setEditing(null)}
          onSave={async (form) => {
            await api.patch(`/cohorts/${editing.code}`, form);
            qc.invalidateQueries({ queryKey: ['cohorts'] });
            setEditing(null);
            toast.success('Cohort updated');
          }}
        />
      )}
    </section>
  );
}

interface CohortFormState {
  code: string;
  name: string;
  description: string | null;
  requiredGroups: GroupServiceType[];
  defaultIndividualServices: IndividualServiceType[];
  maxCapacity: number | null;
  sortOrder: number;
  active: boolean;
}

function CohortEditModal({
  mode,
  codeOriginal,
  initial,
  onClose,
  onSave,
}: {
  mode: 'create' | 'edit';
  codeOriginal?: string;
  initial: CohortFormState;
  onClose: () => void;
  onSave: (form: CreateCohortInput) => Promise<void>;
}) {
  const [form, setForm] = useState<CohortFormState>(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggleGroup(g: GroupServiceType) {
    setForm((f) => ({
      ...f,
      requiredGroups: f.requiredGroups.includes(g)
        ? f.requiredGroups.filter((x) => x !== g)
        : [...f.requiredGroups, g],
    }));
  }
  function toggleIndividual(s: IndividualServiceType) {
    setForm((f) => ({
      ...f,
      defaultIndividualServices: f.defaultIndividualServices.includes(s)
        ? f.defaultIndividualServices.filter((x) => x !== s)
        : [...f.defaultIndividualServices, s],
    }));
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={mode === 'create' ? 'New cohort' : `Edit cohort ${codeOriginal}`}
      size="lg"
    >
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="label">Code</label>
            <input
              className="input"
              placeholder="A"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              disabled={mode === 'edit'}
            />
            <p className="mt-1 text-2xs text-ink-subtle">
              Short alphanumeric identifier. Becomes the prefix of cohortId (e.g. A1, A2).
            </p>
          </div>
          <div>
            <label className="label">Name</label>
            <input
              className="input"
              placeholder="PHP + CPST"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Description (optional)</label>
            <input
              className="input"
              value={form.description ?? ''}
              onChange={(e) => setForm({ ...form, description: e.target.value || null })}
            />
          </div>
          <div>
            <label className="label">Max capacity (optional)</label>
            <input
              type="number"
              className="input"
              min={1}
              placeholder="unlimited"
              value={form.maxCapacity ?? ''}
              onChange={(e) => {
                const v = e.target.value.trim();
                setForm({ ...form, maxCapacity: v ? Math.max(1, Number(v)) : null });
              }}
            />
            <p className="mt-1 text-2xs text-ink-subtle">
              Blocks new intakes / transitions when reached. Blank = unlimited.
            </p>
          </div>
          <div>
            <label className="label">Sort order</label>
            <input
              type="number"
              className="input"
              value={form.sortOrder}
              onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) || 100 })}
            />
          </div>
          <label className="flex items-end gap-2">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            <span className="text-sm">Active (selectable at intake)</span>
          </label>
        </div>

        <div>
          <label className="label">Required GROUP services</label>
          <div className="flex flex-wrap gap-1.5">
            {GroupServiceTypes.map((g) => {
              const on = form.requiredGroups.includes(g);
              return (
                <button
                  type="button"
                  key={g}
                  onClick={() => toggleGroup(g)}
                  className={
                    on
                      ? 'rounded border border-brand-300 bg-brand-50 px-2 py-1 text-xs font-medium text-brand-700'
                      : 'rounded border border-line bg-white px-2 py-1 text-xs text-ink-muted hover:bg-surface-muted'
                  }
                >
                  {ServiceLabels[g as ServiceType]}
                </button>
              );
            })}
          </div>
          <p className="mt-1 text-2xs text-ink-subtle">
            The engine will place each new client in this cohort into one resource per selected group kind.
            (NURSING is always added implicitly.)
          </p>
        </div>

        <div>
          <label className="label">Default required INDIVIDUAL services</label>
          <div className="flex flex-wrap gap-1.5">
            {IndividualServiceTypes.map((s) => {
              const on = form.defaultIndividualServices.includes(s);
              return (
                <button
                  type="button"
                  key={s}
                  onClick={() => toggleIndividual(s)}
                  className={
                    on
                      ? 'rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700'
                      : 'rounded border border-line bg-white px-2 py-1 text-xs text-ink-muted hover:bg-surface-muted'
                  }
                >
                  {ServiceLabels[s as ServiceType]}
                </button>
              );
            })}
          </div>
          <p className="mt-1 text-2xs text-ink-subtle">
            Pre-checked on the intake form. Operators can still add/remove per client.
          </p>
        </div>

        {err && <div className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}
      </div>
      <footer className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn-primary"
          disabled={busy || !form.code || !form.name}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              await onSave({
                code: form.code,
                name: form.name,
                description: form.description,
                requiredGroups: form.requiredGroups,
                defaultIndividualServices: form.defaultIndividualServices,
                maxCapacity: form.maxCapacity,
                sortOrder: form.sortOrder,
                active: form.active,
              });
            } catch (e: any) {
              setErr(e?.response?.data?.message ?? 'Save failed');
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </footer>
    </Modal>
  );
}
